import { Link } from 'react-router';
import { SEO, BreadcrumbSchema, FAQSchema, ServiceSchema, SpeakableSchema } from '@/components/seo/seo';

function RatingBadge({ rating, size = 'md' }: { rating: 'red' | 'amber' | 'green'; size?: 'sm' | 'md' | 'lg' }) {
  const colors = {
    red: 'bg-rose-500 text-white',
    amber: 'bg-amber-500 text-white',
    green: 'bg-emerald-500 text-white',
  };
  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base',
  };
  return (
    <span className={`inline-block font-bold rounded-full uppercase ${colors[rating]} ${sizes[size]}`}>
      {rating}
    </span>
  );
}

function QuoteCard({ quote, source }: { quote: string; source: string }) {
  return (
    <blockquote className="bg-surface-50 border-l-4 border-brand-500 p-6 rounded-r-xl">
      <p className="text-surface-800 italic">&ldquo;{quote}&rdquo;</p>
      <cite className="text-sm text-surface-600 mt-2 block not-italic">— {source}</cite>
    </blockquote>
  );
}

export function DarlingtonCouncilPage() {
  return (
    <div className="min-h-screen">
      <SEO
        title="Darlington Pothole Claims | 38.9% of B/C Roads Amber | Fixtyer"
        description="Darlington Borough Council earns GREEN overall and on spend, yet AMBER on condition — 38.9% of B and C roads are now amber, with 30,261 potholes filled in five years on a 558km network. See what that means for your claim."
        canonical="/councils/darlington"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Councils', url: '/councils' },
          { name: 'Darlington', url: '/councils/darlington' },
        ]}
      />
      <FAQSchema
        faqs={[
          {
            question: 'Darlington has a GREEN overall DfT rating — can I still claim for pothole damage?',
            answer: 'Yes. The overall GREEN scorecard reflects strong spend and asset management, but the DfT Condition rating is AMBER — and that is what matters for your claim. Darlington\'s own 2024 AEI data shows 38.9% of B and C roads in amber ("maintenance may be required soon") and 8% of unclassified roads in red. Section 58 turns on whether the specific defect that damaged your vehicle was reasonably inspected and repaired, not on the headline scorecard colour.',
          },
          {
            question: 'Does the switch to Annual Engineering Inspections (AEI) in 2023 affect my claim?',
            answer: 'It cuts both ways. Darlington adopted AEI in autumn 2023, replacing SCANNER and coarse visual inspection. The council states that figures from 2023 onwards "are not comparable with the previous years." That weakens any Section 58 defence relying on precise year-on-year network knowledge — but it also means you should cite the pre-2023 SCANNER/CVI records for your road type where they show deterioration, and challenge any council attempt to use the post-2023 drop in U-road RED (17% to 4%) as proof roads improved.',
          },
          {
            question: 'What if my pothole was on a residential or unclassified road?',
            answer: 'U-roads make up 363km — 65% of Darlington\'s 558km carriageway network. Under the old coarse visual inspection, 13-17% of U-roads were in RED condition every year from 2020 to 2022. The 2024 AEI survey shows 8% RED, 19% amber and 73% green — but those figures use a different methodology. Residential streets are inspected quarterly for safety defects; back lanes only annually. If your defect was on a U-road, the methodology change and the pre-2023 RED percentages are your strongest structural arguments.',
          },
          {
            question: 'Darlington matches its DfT capital allocation exactly — does that weaken my claim?',
            answer: 'Not necessarily. Darlington projects £3.076m capital spend against a £3.076m DfT allocation in 2025/26 — spend matches allocation pound for pound, unlike councils that overspend significantly. That supports the GREEN spend scorecard. But the council itself estimates 5,875 reactive pothole orders in 2025/26 based on the five-year average, and its planned preventative works on just 9.5km (1.7% of the network) will prevent only an estimated 820 potholes — 14% of the annual average. Reactive volume and AMBER condition coexist.',
          },
          {
            question: 'What does "amber" mean on Darlington\'s B and C roads for my claim?',
            answer: 'Darlington defines amber as "maintenance may be required soon." In 2024, 38.9% of B and C roads — 54km of the 138km classified local network — sat in that category, up from roughly 27% under the old SCANNER surveys. Amber is not yet RED ("should be considered for maintenance"), but it signals the council\'s own survey identified roads approaching intervention. If your pothole was on a B or C road flagged amber in 2024, that is published evidence the council knew maintenance was coming.',
          },
          {
            question: 'Darlington filled 6,341 potholes in 2024/25 — does that mean the roads are fixed?',
            answer: 'No. Darlington filled 6,341 potholes in 2024/25 — 45% of its reactive maintenance budget (£321k) — yet the DfT still rates condition AMBER. Over five years the council filled 30,261 potholes on a 558km network, averaging roughly 17 repairs every day. The council\'s own case study from 2014 resurfacing shows 257 potholes in the 12 months before treatment versus 15 in the year after — reactive repairs treat symptoms; they do not reset network condition.',
          },
          {
            question: 'How often does Darlington inspect the road where I had my incident?',
            answer: 'Darlington\'s report states town centre streets are inspected monthly, main roads monthly, residential streets quarterly, and back lanes annually. Safety defects from these inspections plus reports via the public Report It website are ordered for repair. Section 58 defences rely on this inspection regime being reasonable for the road type — quarterly residential inspection means a pothole could form and cause damage between visits, especially if prior reports were not acted on promptly.',
          },
        ]}
      />
      <ServiceSchema
        councilName="Darlington Borough Council"
        councilSlug="darlington"
        rating="green"
      />
      <SpeakableSchema
        url="/councils/darlington"
        headline="Darlington Road Maintenance Report"
        cssSelectors={['[data-speakable="summary"]', 'h1', 'h2']}
      />

      {/* Hero */}
      <section className="relative bg-brand-950 text-white overflow-hidden">
        <div
          className="absolute top-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`,
          }}
        />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <nav className="text-sm text-brand-400 mb-8 flex items-center gap-2">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/councils" className="hover:text-white transition-colors">Councils</Link>
            <span>/</span>
            <span className="text-white">Darlington</span>
          </nav>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <RatingBadge rating="green" size="lg" />
            <span className="text-brand-300">Overall</span>
            <span className="text-brand-500">|</span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Condition</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="green" size="sm" /> <span className="text-brand-300 text-sm">Spend</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Best Practice</span>
            </span>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight mb-6">
            Darlington: 38.9% of B and C Roads Now Amber
          </h1>

          <p className="text-xl text-brand-200 max-w-2xl mb-8 leading-relaxed" data-speakable="summary">
            Darlington Borough Council earns an overall <strong className="text-emerald-400">GREEN</strong> DfT
            scorecard and matches its capital allocation to the penny — yet condition is rated{' '}
            <strong className="text-amber-400">AMBER</strong> because{' '}
            <strong className="text-amber-400">38.9% of B and C roads</strong> now sit in the council&apos;s own
            &ldquo;maintenance may be required soon&rdquo; category, while{' '}
            <strong className="text-rose-400">30,261 potholes were filled in five years</strong> on a network
            just 558km long.
          </p>

          <div className="bg-amber-500/20 border border-amber-500/30 rounded-2xl p-6 max-w-md">
            <div className="text-6xl font-bold text-amber-400">38.9%</div>
            <div className="text-white font-medium mt-2">B and C roads in amber (2024)</div>
            <div className="text-brand-300 text-sm mt-1">
              Up from roughly 27% under SCANNER — 54km of Darlington&apos;s 138km classified local road
              network flagged for imminent maintenance under the new AEI survey.
            </div>
          </div>
        </div>

        <div
          className="absolute bottom-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #1e40af,
              #1e40af 40px,
              #f97316 40px,
              #f97316 80px
            )`,
          }}
        />
      </section>

      {/* The Condition Picture */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What the condition data shows
          </h2>
          <p className="text-surface-600 mb-8">
            Five years of survey data from Darlington&apos;s own transparency report — with a methodology break in 2023
          </p>

          {/* A-roads */}
          <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">A-roads (57km — 10% of network): volatile under AEI</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-surface-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-surface-100 border-b border-surface-200">
                    <th className="px-4 py-2 text-left font-semibold text-brand-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">1.9%</td>
                    <td className="px-4 py-2 text-center">15.4%</td>
                    <td className="px-4 py-2 text-center">82.8%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2021</td>
                    <td className="px-4 py-2 text-center">1.3%</td>
                    <td className="px-4 py-2 text-center">13.0%</td>
                    <td className="px-4 py-2 text-center">85.8%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">1.5%</td>
                    <td className="px-4 py-2 text-center">12.0%</td>
                    <td className="px-4 py-2 text-center">86.5%</td>
                  </tr>
                  <tr className="border-b border-amber-100 bg-amber-50">
                    <td className="px-4 py-2 font-mono">2023*</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">7.2%</td>
                    <td className="px-4 py-2 text-center font-bold text-amber-600">33.5%</td>
                    <td className="px-4 py-2 text-center">59.25%</td>
                  </tr>
                  <tr className="bg-amber-50">
                    <td className="px-4 py-2 font-mono font-bold">2024*</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">4.5%</td>
                    <td className="px-4 py-2 text-center font-bold text-amber-600">29.0%</td>
                    <td className="px-4 py-2 text-center font-bold">66.5%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-surface-600 text-sm">
              *AEI methodology from 2023 — not comparable with SCANNER years. A-road RED jumped from 1.5% to 7.2%
              the year the survey changed, then eased to 4.5%. Green A-road condition under the old system was above
              85%; under AEI it has not recovered past 66.5%.
            </p>
          </div>

          {/* B/C roads */}
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-amber-900 mb-4">B and C roads (138km — 25% of network): amber rising</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-amber-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-amber-100 border-b border-amber-200">
                    <th className="px-4 py-2 text-left font-semibold text-amber-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">6.0%</td>
                    <td className="px-4 py-2 text-center">28.5%</td>
                    <td className="px-4 py-2 text-center">65.5%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2021</td>
                    <td className="px-4 py-2 text-center">5.4%</td>
                    <td className="px-4 py-2 text-center">27.1%</td>
                    <td className="px-4 py-2 text-center">67.5%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">5.3%</td>
                    <td className="px-4 py-2 text-center">26.3%</td>
                    <td className="px-4 py-2 text-center">68.4%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2023*</td>
                    <td className="px-4 py-2 text-center">4.7%</td>
                    <td className="px-4 py-2 text-center">27.4%</td>
                    <td className="px-4 py-2 text-center">67.9%</td>
                  </tr>
                  <tr className="bg-amber-50">
                    <td className="px-4 py-2 font-mono font-bold">2024*</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">2.6%</td>
                    <td className="px-4 py-2 text-center font-bold text-amber-600">38.9%</td>
                    <td className="px-4 py-2 text-center font-bold">58.5%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-amber-800 font-medium">
              B/C amber surged to 38.9% in 2024 while green fell to 58.5% — the lowest on record in either
              methodology. Combined amber and red, 41.5% of B and C roads need or will soon need maintenance.
            </p>
          </div>

          {/* Spend context */}
          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-2xl font-bold mb-4">GREEN spend — allocation matched exactly</h3>
            <div className="grid sm:grid-cols-3 gap-6 mb-6">
              <div>
                <div className="text-3xl font-bold text-emerald-400">£3.076m</div>
                <div className="text-brand-300 text-sm">DfT capital allocation 2025/26</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-emerald-400">£3.076m</div>
                <div className="text-brand-300 text-sm">Projected capital spend 2025/26</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-emerald-400">80%</div>
                <div className="text-brand-300 text-sm">Estimated preventative share</div>
              </div>
            </div>
            <p className="text-xl font-semibold text-white">
              Darlington spends exactly its DfT allocation — not a penny more — and targets 80% preventative
              maintenance. The GREEN spend scorecard is earned. But condition remains{' '}
              <span className="text-amber-400">AMBER</span> because the roads are still producing thousands of
              reactive repairs every year on a network where two in five B and C roads are flagged amber.
            </p>
          </div>
        </div>
      </section>

      {/* The U-road picture */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The 363km residential majority
          </h2>
          <p className="text-surface-600 mb-8">
            65% of Darlington&apos;s carriageway network is unclassified roads — and the survey ruler changed mid-stream
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-rose-700">Red</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-amber-700">Amber</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-emerald-700">Green</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-surface-500">Survey</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">13%</td>
                  <td className="px-6 py-4 text-center text-surface-400 italic">n/a</td>
                  <td className="px-6 py-4 text-center text-surface-400 italic">n/a</td>
                  <td className="px-6 py-4 text-center text-surface-500">CVI</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">16%</td>
                  <td className="px-6 py-4 text-center text-surface-400 italic">n/a</td>
                  <td className="px-6 py-4 text-center text-surface-400 italic">n/a</td>
                  <td className="px-6 py-4 text-center text-surface-500">CVI</td>
                </tr>
                <tr className="border-b border-surface-100 bg-rose-50">
                  <td className="px-6 py-4 font-mono text-brand-900">2022</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">17%</td>
                  <td className="px-6 py-4 text-center text-surface-400 italic">n/a</td>
                  <td className="px-6 py-4 text-center text-surface-400 italic">n/a</td>
                  <td className="px-6 py-4 text-center text-surface-500">CVI</td>
                </tr>
                <tr className="border-b border-surface-100 bg-amber-50">
                  <td className="px-6 py-4 font-mono text-brand-900">2023*</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">4%</td>
                  <td className="px-6 py-4 text-center">11%</td>
                  <td className="px-6 py-4 text-center">84%</td>
                  <td className="px-6 py-4 text-center text-amber-600">AEI</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-mono font-bold text-brand-900">2024*</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">8%</td>
                  <td className="px-6 py-4 text-center font-bold text-amber-600">19%</td>
                  <td className="px-6 py-4 text-center font-bold">73%</td>
                  <td className="px-6 py-4 text-center text-amber-600">AEI</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">The CVI-to-AEI cliff</h3>
              <p className="text-surface-600 text-sm mb-3">
                Under coarse visual inspection, U-road RED condition climbed from 13% to 17% between 2020 and 2022 —
                roughly 47-62km of residential streets in the worst category. When AEI replaced CVI in 2023, RED
                apparently fell to 4%, then rose to 8% in 2024 with 19% amber.
              </p>
              <p className="text-surface-600 text-sm">
                Darlington&apos;s own report states the two latest years &ldquo;are not comparable with the previous
                three.&rdquo; A drop from 17% to 4% is a new ruler, not a recovery.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">What AEI adds for U-roads</h3>
              <p className="text-amber-800 text-sm mb-3">
                For the first time, Darlington publishes amber and green splits for unclassified roads — 19% amber
                and 8% RED in 2024 means 27% of the 363km U-road network (roughly 98km) needs or will soon need
                maintenance.
              </p>
              <p className="text-amber-800 text-sm">
                The entire network — A, B, C and U — is now surveyed annually under AEI. That is an improvement in
                coverage, but the historical CVI record still documents sustained RED levels before the switch.
              </p>
            </div>
          </div>

          <QuoteCard
            quote="As we have changed survey method the two latest years (2023 onwards) figures are not comparable with the previous years."
            source="Darlington Borough Council Local Highways Maintenance Transparency Report (June 2025)"
          />

          <div className="mt-8 bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">Why this matters for Section 58</h3>
            <p className="text-brand-200 mb-4">
              To rely on the Section 58 defence, Darlington must show it had a reasonable system for knowing road
              condition. For U-roads, ask:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Was your road in the 13-17% RED band under CVI before the methodology changed?</li>
              <li>• If the council now claims 8% RED under AEI, can it explain the 17% → 4% cliff in 2023?</li>
              <li>• Residential streets are safety-inspected quarterly — was your defect visible between inspections?</li>
              <li>• Back lanes are inspected only annually — a longer blind window for defects to form undetected</li>
            </ul>
            <p className="text-white font-medium">
              A council that changes how it measures condition cannot use the new figures to erase the old ones —
              especially when its own footnote says they are not comparable.
            </p>
          </div>
        </div>
      </section>

      {/* Pothole repairs */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            30,261 potholes in five years
          </h2>
          <p className="text-surface-600 mb-8">
            Reactive repair volume on a compact 558km network — roughly 54 potholes per kilometre since 2020/21
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Potholes filled</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Five-year share</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020/21</td>
                  <td className="px-6 py-4 text-center">5,942</td>
                  <td className="px-6 py-4 text-center text-surface-500">19.6%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021/22</td>
                  <td className="px-6 py-4 text-center">6,544</td>
                  <td className="px-6 py-4 text-center text-surface-500">21.6%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022/23</td>
                  <td className="px-6 py-4 text-center">5,332</td>
                  <td className="px-6 py-4 text-center text-surface-500">17.6%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023/24</td>
                  <td className="px-6 py-4 text-center">6,102</td>
                  <td className="px-6 py-4 text-center text-surface-500">20.2%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2024/25</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">6,341</td>
                  <td className="px-6 py-4 text-center text-surface-500">21.0%</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-6 py-4 font-bold text-brand-900">Five-year total</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">30,261</td>
                  <td className="px-6 py-4 text-center font-bold">100%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">~17 repairs every day</h3>
              <p className="text-surface-600 text-sm">
                Averaged over five years, Darlington fills roughly 17 potholes per day across its 558km network.
                In 2024/25 alone, 45% of the reactive maintenance budget — £321,000 — went on 6,341 pothole
                repairs. A network producing defects at that rate is one where potholes routinely form between
                inspections.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">5,875 more expected in 2025/26</h3>
              <p className="text-amber-800 text-sm">
                Darlington estimates its highway safety inspectors will issue orders for 5,875 reactive potholes
                in 2025/26 based on the previous five-year average. Planned preventative works on 9.5km — just
                1.7% of the network — will prevent an estimated 820 potholes: 14% of the annual average.
              </p>
            </div>
          </div>

          <QuoteCard
            quote="When used at the correct time, surface treatments are fit for purpose and last at least as long as its anticipated life cycle (8-10years), demonstrated by the fact that there are significantly less potholes after resurfacing than in the year prior. For example, in the streets highlighted for resurfacing in 2014 there we're 257 potholes in the 12 months prior to surface treatment. However in the following 12 months after application there was only 15, and a total of 184 potholes for the whole 10 years since."
            source="Darlington Borough Council Local Highways Maintenance Transparency Report (June 2025)"
          />
        </div>
      </section>

      {/* AEI and asset management */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The AEI switch and the GREEN-overall tension
          </h2>
          <p className="text-surface-600 mb-8">
            Darlington&apos;s own explanation for its survey change — and why overall GREEN does not mean roads are green
          </p>

          <div className="space-y-6 mb-8">
            <QuoteCard
              quote="In a bid to gain a more detailed understanding of the highway network, its condition and treatment requirements, in the autumn of 2023 DBC started to use Annual Engineering Inspections (AEI) that look at treatment selection as opposed to a simple condition score. Following an on-site assessment, the AEI survey enables maintenance schemes and treatments to be identified easily and efficiently."
              source="Darlington Borough Council Local Highways Maintenance Transparency Report (June 2025)"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">What GREEN overall actually means</h3>
              <p className="text-surface-600 text-sm mb-3">
                Darlington earns GREEN on overall and spend because it matches its DfT allocation, targets 80%
                preventative maintenance, and runs a documented five-year forward programme from AEI data. Revenue
                spend is projected at £1.172m in 2025/26 with 31% of the 2024/25 revenue budget on surface
                treatments.
              </p>
              <p className="text-surface-600 text-sm">
                Best practice is AMBER — the council leads the North East Highways Surfacing Framework via NEPO
                and trials warm-mix materials, but DfT best practice turns on published asset management evidence
                and PAS 2161 transition readiness, not procurement leadership alone.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">Why condition stays AMBER</h3>
              <ul className="text-amber-800 text-sm space-y-2">
                <li>• 38.9% of B/C roads in amber — highest amber share on record</li>
                <li>• A-road green fell from 86.5% (SCANNER) to 66.5% (AEI)</li>
                <li>• 27% of U-roads in amber or red under the 2024 AEI survey</li>
                <li>• 30,261 potholes filled in five years despite 80% preventative spend target</li>
                <li>• CPM recommends 60/40 structural/surface split; council runs 80/20</li>
              </ul>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <h3 className="font-semibold text-brand-900 px-6 py-4 bg-surface-100 border-b border-surface-200">
              Planned capital works 2025/26
            </h3>
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-surface-50 border-b border-surface-200">
                  <th className="px-6 py-3 text-left font-semibold text-brand-900">Road class</th>
                  <th className="px-6 py-3 text-center font-semibold text-brand-900">Schemes</th>
                  <th className="px-6 py-3 text-center font-semibold text-brand-900">Total length</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4">A roads</td>
                  <td className="px-6 py-4 text-center">4</td>
                  <td className="px-6 py-4 text-center">2.4km</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4">B roads</td>
                  <td className="px-6 py-4 text-center">2</td>
                  <td className="px-6 py-4 text-center">0.43km</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4">C roads</td>
                  <td className="px-6 py-4 text-center">1</td>
                  <td className="px-6 py-4 text-center">2.5km</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4">Unclassified roads</td>
                  <td className="px-6 py-4 text-center">14</td>
                  <td className="px-6 py-4 text-center">4.2km</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-6 py-4 font-bold">Total carriageway</td>
                  <td className="px-6 py-4 text-center font-bold">21</td>
                  <td className="px-6 py-4 text-center font-bold">9.5km (1.7% of network)</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">Inspection frequencies</h3>
            <p className="text-brand-200 mb-4">
              Darlington&apos;s reactive programme is driven by routine safety inspections on every street:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Town centre streets — monthly</li>
              <li>• Main roads — monthly</li>
              <li>• Residential streets — quarterly</li>
              <li>• Back lanes — annually</li>
              <li>• Public reports via Report It — ordered for repair alongside inspection findings</li>
            </ul>
            <p className="text-white font-medium">
              Frequencies follow the national code of practice. Your claim turns on whether the specific defect
              should have been found at the last inspection for your road type — or was already reported and not
              repaired in time.
            </p>
          </div>
        </div>
      </section>

      {/* What GREEN + AMBER condition means for claims */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Claiming against a GREEN-overall, AMBER-condition council
          </h2>
          <p className="text-surface-600 mb-8">
            Honest assessment: Darlington runs a disciplined maintenance programme — here&apos;s how that changes your approach
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-surface-50 rounded-2xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-4">What works in the council&apos;s favour</h3>
              <ul className="text-surface-700 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> GREEN overall and spend scorecards — allocation matched, 80% preventative target
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Full-network annual AEI survey from 2023 — better coverage than CVI-only U-roads
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Documented five-year forward programme with condition projection modelling
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Code-of-practice inspection frequencies with public Report It integration
                </li>
              </ul>
              <p className="text-surface-500 text-sm mt-4 italic">
                Expect a well-documented Section 58 defence on main roads with monthly inspections. Generic claims will struggle.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <h3 className="font-semibold text-amber-900 mb-4">What works in yours</h3>
              <ul className="text-amber-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> AMBER condition — 38.9% of B/C roads flagged amber in 2024
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> U-road RED was 13-17% under CVI before the methodology changed
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Council admits 2023+ figures are not comparable — weakens precise network-knowledge defences
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 30,261 potholes in five years — defects form faster than preventative works reach most roads
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Back lanes inspected only annually — longest gap between safety checks
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">The winning strategy here is specificity</h3>
            <p className="text-brand-200 mb-4">
              Against a council with GREEN overall and spend scorecards, your claim lives or dies on the{' '}
              <strong className="text-white">specific defect</strong>:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Prior reports of the same pothole (Report It, FixMyStreet) — proof of actual notice</li>
              <li>• Photos showing the defect&apos;s size, depth and visible age (weathered edges, previous patching)</li>
              <li>• The road&apos;s class — on a U-road, cite pre-2023 CVI RED percentages and the methodology break</li>
              <li>• On B/C roads, cite the 38.9% amber figure as published evidence maintenance was due</li>
              <li>• Back lanes — challenge annual inspection as reasonable if the defect was reportable earlier</li>
            </ul>
            <p className="text-white font-medium">
              Mac builds exactly this case: he searches for prior reports, assesses your photo evidence, and
              cites Darlington&apos;s own transparency data where it helps you.
            </p>
          </div>
        </div>
      </section>

      {/* Report a pothole CTA */}
      <section className="py-16 bg-white border-y border-surface-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-4">
            Report the pothole to Darlington first
          </h2>
          <p className="text-surface-600 mb-6">
            Darlington&apos;s transparency report states that safety defects identified on routine inspections plus
            reports via the public Report It website are ordered for repair. Reporting the defect through the
            council creates a dated record — useful evidence if the pothole was reported before your incident,
            or if the council failed to repair it within a reasonable time.
          </p>
          <a
            href="https://highwaysreporting.darlington.gov.uk/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center px-8 py-4 bg-brand-900 text-white font-semibold rounded-xl hover:bg-brand-800 transition-colors"
          >
            Report a pothole to Darlington Council
            <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </a>
          <p className="text-surface-500 text-sm mt-4">
            Use the council&apos;s Report It portal. Keep your reference number and any confirmation emails.
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-bold mb-4">
            Hit a pothole in Darlington?
          </h2>
          <p className="text-xl text-brand-200 mb-8">
            A GREEN-overall council still needs a precise claim when condition is AMBER. £35 for a professional claim pack.
          </p>

          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <div className="bg-brand-900/50 rounded-xl p-6 border border-brand-800">
              <h3 className="font-semibold text-brand-300 mb-3">DIY claim</h3>
              <ul className="text-sm text-brand-400 space-y-1 text-left">
                <li>• Submit photos and invoices</li>
                <li>• Use generic template letter</li>
                <li>• No AEI methodology-break argument</li>
                <li>• No prior-report search</li>
                <li>• No B/C amber condition cited</li>
              </ul>
            </div>
            <div className="bg-accent-500/20 rounded-xl p-6 border border-accent-500/30">
              <h3 className="font-semibold text-white mb-3">Professional claim pack</h3>
              <ul className="text-sm text-brand-200 space-y-1 text-left">
                <li>✅ 38.9% B/C amber condition documented</li>
                <li>✅ CVI-to-AEI methodology break argued</li>
                <li>✅ 30,261 repairs in five years cited</li>
                <li>✅ Prior reports searched and attached</li>
                <li>✅ Section 58 rebuttal tailored to Darlington</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/check?council=darlington"
              className="inline-flex items-center justify-center px-8 py-4 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-all hover:shadow-lg hover:shadow-accent-500/25"
            >
              Order my Darlington claim pack — £35
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              to="/road-maintenance-report"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors"
            >
              View all council ratings
            </Link>
          </div>

          <p className="text-brand-400 text-sm mt-6">
            No percentage fees. You keep 100% of any compensation.
          </p>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-8">
            Frequently asked questions
          </h2>

          <div className="space-y-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Darlington has a GREEN overall DfT rating — can I still claim for pothole damage?
              </h3>
              <p className="text-surface-600 text-sm">
                Yes. The overall GREEN scorecard reflects strong spend and asset management, but the DfT Condition
                rating is AMBER — and that is what matters for your claim. Darlington&apos;s own 2024 AEI data shows
                38.9% of B and C roads in amber and 8% of unclassified roads in red. Section 58 turns on whether
                the specific defect that damaged your vehicle was reasonably inspected and repaired, not on the
                headline scorecard colour.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Does the switch to Annual Engineering Inspections (AEI) in 2023 affect my claim?
              </h3>
              <p className="text-surface-600 text-sm">
                It cuts both ways. Darlington adopted AEI in autumn 2023, replacing SCANNER and coarse visual
                inspection. The council states that figures from 2023 onwards &ldquo;are not comparable with the
                previous years.&rdquo; That weakens any Section 58 defence relying on precise year-on-year network
                knowledge — but it also means you should cite the pre-2023 SCANNER/CVI records where they show
                deterioration, and challenge any council attempt to use the post-2023 drop in U-road RED as proof
                roads improved.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                What if my pothole was on a residential or unclassified road?
              </h3>
              <p className="text-surface-600 text-sm">
                U-roads make up 363km — 65% of Darlington&apos;s 558km network. Under coarse visual inspection,
                13-17% of U-roads were in RED condition from 2020 to 2022. The 2024 AEI survey shows 8% RED,
                19% amber and 73% green — but those figures use a different methodology. Residential streets are
                inspected quarterly for safety defects; back lanes only annually.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Darlington matches its DfT capital allocation exactly — does that weaken my claim?
              </h3>
              <p className="text-surface-600 text-sm">
                Not necessarily. Darlington projects £3.076m capital spend against a £3.076m DfT allocation in
                2025/26. That supports the GREEN spend scorecard. But the council estimates 5,875 reactive pothole
                orders in 2025/26, and planned preventative works on 9.5km will prevent only an estimated 820
                potholes — 14% of the annual average. Reactive volume and AMBER condition coexist.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                What does &ldquo;amber&rdquo; mean on Darlington&apos;s B and C roads for my claim?
              </h3>
              <p className="text-surface-600 text-sm">
                Darlington defines amber as &ldquo;maintenance may be required soon.&rdquo; In 2024, 38.9% of B
                and C roads sat in that category, up from roughly 27% under SCANNER. If your pothole was on a B or
                C road flagged amber, that is published evidence the council knew maintenance was approaching.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Darlington filled 6,341 potholes in 2024/25 — does that mean the roads are fixed?
              </h3>
              <p className="text-surface-600 text-sm">
                No. Darlington filled 6,341 potholes in 2024/25 — 45% of its reactive budget (£321k) — yet
                condition remains AMBER. Over five years the council filled 30,261 potholes on 558km, averaging
                roughly 17 repairs every day. Reactive repairs treat symptoms; they do not reset network condition.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                How often does Darlington inspect the road where I had my incident?
              </h3>
              <p className="text-surface-600 text-sm">
                Town centre streets and main roads are inspected monthly. Residential streets are inspected
                quarterly. Back lanes are inspected annually. Safety defects from these inspections plus Report It
                submissions are ordered for repair. Quarterly residential inspection means a pothole could form and
                cause damage between visits — especially if prior reports were not acted on promptly.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Attribution Footer */}
      <section className="py-8 bg-surface-100 border-t border-surface-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs text-surface-500 text-center">
            Data sources:{' '}
            <a
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a>{' '}
            |{' '}
            <a
              href="https://www.darlington.gov.uk/transport-roads-and-parking/local-highway-maintenance-transparency-report/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Darlington Borough Council Local Highways Maintenance Transparency Report (June 2025)
            </a>
            . Contains public sector information licensed under the{' '}
            <a
              href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Open Government Licence v3.0
            </a>
            .
          </p>
        </div>
      </section>
    </div>
  );
}
